'use client';

import {MeinText} from '@components/@typography/MeinText';
import {MeinDivider} from '@components/@ui';
import {useVideoPlayer, useVideoPlayerState} from '@components/@ui/VideoPlayer';
import {ActivityService} from '@services/activity/activity.service';
import {emitUserInteractionEvent} from '@utils/emitUserInteractionEvent';
import {formatTime} from '@utils/formatTime';
import classNames from 'classnames';
import {useTranslations} from 'next-intl';
import {FC, useMemo} from 'react';
import {useChapterCues} from './@hooks/useChapterCues';
import {useChaptersStore} from './@hooks/useChaptersStore';
import styles from './Chapters.module.css';

interface IChapterMark {
    start_timestamp: string;
    end_timestamp: string;
    chapter_title: string;
    chapter_description: string;
}

interface IProps {
    chapterMarks: IChapterMark[];
    duration: number;
    videoId: number;
    onChapterClick?: (startTime: number) => void;
    liveEventId: number;
    className?: string;
}

const Chapters: FC<IProps> = ({
    chapterMarks,
    duration,
    videoId,
    onChapterClick,
    className,
    liveEventId,
}) => {
    const {seek, play} = useVideoPlayer();
    const t = useTranslations('chapters');
    const {cues} = useChapterCues(chapterMarks, duration);

    const isPlaying = useVideoPlayerState(s => s.isPlaying);
    const isStarted = useVideoPlayerState(s => s.isStarted);
    const isReady = useVideoPlayerState(s => s.isReady);
    const shouldPlay = isReady && (isPlaying || !isStarted);
    const currentTime = useVideoPlayerState(s => s.currentTime);

    const activeChapterTimestamp = useChaptersStore(
        s => s.activeChapterTimestamp,
    );
    const chaptersVideoId = useChaptersStore(s => s.videoId);

    // Only show active chapter if this component is for the current video
    const shouldShowActiveChapter =
        videoId === undefined || videoId === chaptersVideoId;

    const handleChapterClick = (startTime: number, index: number) => {
        emitUserInteractionEvent();
        seek(startTime);
        if (shouldPlay) {
            play();
        }

        onChapterClick?.(startTime);

        ActivityService.logElementClick({
            resource_type: 'chapter_mark',
            resource_id: `chapter_${index + 1}`,
            live_event_id: liveEventId.toString(),
            chapter_timestamp: formatTime(startTime),
            chapter_title: chapterMarks[index].chapter_title,
            playback_position: Math.round(currentTime),
        });
    };

    const activeChapterIndex = useMemo(() => {
        if (!shouldShowActiveChapter) {
            return -1;
        }

        return cues.findIndex(
            cue =>
                activeChapterTimestamp >= cue.startTime &&
                activeChapterTimestamp < cue.endTime,
        );
    }, [activeChapterTimestamp, cues, shouldShowActiveChapter]);

    return (
        <div className={classNames(styles.chaptersContainer, className)}>
            <MeinText
                type="headline"
                tag="span"
                size="s"
                className={styles.panelHeader}
            >
                {t('title')}
            </MeinText>
            <MeinDivider />
            <div className={styles.scrollContainer}>
                <div className={styles.chaptersList}>
                    {cues.map((chapterMark, i) => {
                        return (
                            <button
                                key={'c' + chapterMark.title + i}
                                className={classNames(styles.chapter, {
                                    [styles.active]: activeChapterIndex === i,
                                })}
                                onClick={() => {
                                    handleChapterClick(
                                        chapterMark.startTime,
                                        i,
                                    );
                                }}
                            >
                                <MeinText
                                    type="title"
                                    tag="span"
                                    size="m"
                                    className={styles.chapterTimestamp}
                                >
                                    {formatTime(chapterMark.startTime)}
                                </MeinText>
                                <MeinText
                                    size="m"
                                    linesToClamp={2}
                                    className={styles.chapterTitle}
                                >
                                    {chapterMark.title}
                                </MeinText>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Chapters;
